import { EndpointInterface } from '../../core'
import {
  DEFAULT_AUTH_API_VERSION,
  ENDPOINT_AUTH_LOGIN_OPTIONS,
  ENDPOINT_AUTH_LOGIN_PASSWORD,
  ENDPOINT_AUTH_LOGIN_PIN,
  ENDPOINT_AUTH_LOGIN_TOKEN_EXTERNAL,
  ENDPOINT_AUTH_MANAGEMENT_CHANGE_PASSWORD,
  ENDPOINT_AUTH_MANAGEMENT_FORGOT_PASSWORD,
  ENDPOINT_AUTH_MFA_DISABLE,
  ENDPOINT_AUTH_MFA_SELF_DISABLE,
  ENDPOINT_AUTH_MFA_SETUP_INITIATE,
  ENDPOINT_AUTH_MFA_SETUP_VERIFY,
  ENDPOINT_AUTH_MFA_VERIFY,
  ENDPOINT_AUTH_TOKEN_EXCHANGE,
  ENDPOINT_AUTH_TOKEN_REFRESH,
  ENDPOINT_AUTH_TOKEN_REVOKE,
} from '../constants'
import {
  AuthManagementChangePasswordPostPayload,
  AuthManagementChangePasswordPostResponse,
  AuthLoginOptionsPostResponse,
  AuthLoginPasswordPostPayload,
  AuthLoginPasswordPostResponse,
  AuthMfaVerifyPostPayload,
  AuthMfaVerifyPostResponse,
  AuthTokenExchangePostPayload,
  AuthTokenExchangePostResponse,
  AuthTokenRefreshPostResponse,
  AuthTokenRefreshPostPayload,
  AuthManagementTokenRevokePostPayload,
  AuthMfaSetupInitiatePostPayload,
  AuthMfaSetupVerifyPostPayload,
  AuthMfaSetupInitiatePostResponse,
  AuthMfaDisablePostPayload,
  AuthLoginPinPostResponse,
  AuthLoginTokenExternalPostPayload,
  AuthLoginTokenExternalPostResponse,
} from '../types'

export const postAuthLoginOptions =
  (
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<AuthLoginOptionsPostResponse> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_LOGIN_OPTIONS,
        params: [version],
      },
      method: 'post',
    })

export const postAuthLoginPassword =
  (
    data: AuthLoginPasswordPostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<AuthLoginPasswordPostResponse> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_LOGIN_PASSWORD,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthLoginPin =
  (
    data: AuthLoginPasswordPostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<AuthLoginPinPostResponse> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_LOGIN_PIN,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthMfaSetupInitiate =
  (
    data: AuthMfaSetupInitiatePostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<AuthMfaSetupInitiatePostResponse> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_MFA_SETUP_INITIATE,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthMfaSetupVerify =
  (
    data: AuthMfaSetupVerifyPostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<{}> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_MFA_SETUP_VERIFY,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthMfaVerify =
  (
    data: AuthMfaVerifyPostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<AuthMfaVerifyPostResponse> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_MFA_VERIFY,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthTokenExchange =
  (
    data: AuthTokenExchangePostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<AuthTokenExchangePostResponse> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_TOKEN_EXCHANGE,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthTokenRefresh =
  (
    data: AuthTokenRefreshPostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<AuthTokenRefreshPostResponse> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_TOKEN_REFRESH,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthTokenRevoke =
  (
    data: AuthManagementTokenRevokePostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<void> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_TOKEN_REVOKE,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthManagementForgotPassword =
  (
    data: AuthManagementChangePasswordPostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<AuthManagementChangePasswordPostResponse> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_MANAGEMENT_FORGOT_PASSWORD,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthManagementChangePassword =
  (
    data: AuthManagementChangePasswordPostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<AuthManagementChangePasswordPostResponse> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_MANAGEMENT_CHANGE_PASSWORD,
        params: [version],
      },
      method: 'post',
      data,
    })

export const postAuthManagementMfaDisable =
  (
    clinicId: number,
    data: AuthMfaDisablePostPayload,
    version: string = DEFAULT_AUTH_API_VERSION,
  ): EndpointInterface<void> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_MFA_DISABLE,
        params: [version, clinicId],
      },
      method: 'post',
      data,
    })

export const postAuthManagementMfaSelfDisable =
  (version: string = DEFAULT_AUTH_API_VERSION): EndpointInterface<void> =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_MFA_SELF_DISABLE,
        params: [version],
      },
      method: 'post',
    })

export const postAuthLoginTokenExternal =
  ({
    data,
    apiVersion = DEFAULT_AUTH_API_VERSION,
  }: {
    data: AuthLoginTokenExternalPostPayload
    apiVersion?: string
  }): EndpointInterface<
    AuthLoginTokenExternalPostResponse,
    AuthLoginTokenExternalPostPayload
  > =>
  (provider) =>
    provider({
      url: {
        endpoint: ENDPOINT_AUTH_LOGIN_TOKEN_EXTERNAL,
        params: [apiVersion],
      },
      method: 'post',
      data,
    })
